/**
 * Receiving Log Component
 * -----------------------
 * This component handles the display and interaction with the receiving logs. It fetches logs based on the selected location,
 * configures the table and UI text, and provides forms for filtering and editing log entries.
 *
 * Features:
 * - Fetches and displays logs based on the selected location.
 * - Configures the table and UI text using data from the assets data service.
 * - Provides forms for filtering logs and editing log entries.
 * - Allows toggling between viewing archived logs and logs with "date out" included.
 * - Updates logs based on user input and form submissions.
 *
 */

/**
 * @component ReceivingLogComponent
 * -------------------------------
 * Manages the display and interaction with the receiving logs, fetching logs based on the selected location,
 * configuring the table and UI text, and providing forms for filtering and editing log entries.
 * Users can toggle between viewing archived logs and logs with "date out" included.
 *
 * This component ensures real-time updates to the log data and UI elements based on user input,
 * form submissions, and service responses.
 *
 * Subscriptions:
 * - `isLargeScreenSubscription`: Tracks screen size changes to adjust UI layout dynamically.
 * - `logSubscription`: Listens for updates to the receiving logs in the log service and updates the table accordingly.
 * - `selectedLocationSubscription`: Responds to location changes in the selected location service, updating log data and UI text.
 * - `logsLoadingSubscription`: Monitors the loading state of log data through the log service to provide user feedback.
 *
 * Notes:
 * - This component relies on multiple services (`LogService`, `AssetsDataService`, `ScreenSizeService`)
 *   to fetch and update log data dynamically.
 * - Subscriptions play a key role in managing real-time updates, ensuring the displayed logs and
 *   UI elements remain in sync with user interactions and backend responses.
 * - Potential improvement: Consider consolidating or refactoring subscription handling to
 *   improve maintainability, potentially leveraging operators like `combineLatest` for efficiency.
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { retryWhen, Subscription } from 'rxjs';
import { ReceivingLogStateService } from '../../../domain/receiving-log/receiving-log-state.service';
import { ToastService } from '../../../service/toast.service';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { ReceivingLogSearch } from '../../../models/logs/ReceivingLogSearch';
import { ReceivingLogRow } from '../../../models/logs/ReceivingLogRow';
import { ApiReceivingLogRow } from '../../../models/logs/ApiReceivingLogRow';
import { GetReceivingLogsResponse } from '../../../models/logs/GetReceivingLogResponse';

import { AssetsDataService } from '../../../service/assets-data.service';
import { PhotoService } from '../../../service/photo.service';
import { EmailService } from '../../../service/email.service';
import { EmailData } from '../../../models/email/EmailData';
import { Location } from '../../../models/assets-data/LocationConfig.interface';
import { EntraService } from '../../../service/entra.service';
import { AlertController } from '@ionic/angular';

import { TableField } from '../../../models/assets-data/receiving/ReceivingLogTableConfig.interface';
import { DataLoading } from '../../../models/DataLoading.interface';
import { formatDateString } from '../../../utils/date-functions';

import {
  ToastText,
  AlertText,
} from '../../../models/assets-data/UiConfig.interface';
import { SelectedLocation } from '../../../models/selectedLocation.interface';
import { PhotoMetadata } from '../../../models/logs/photos/PhotoMetadata';
import { LogEntryPhoto } from '../../../models/logs/photos/LogEntryPhoto.interface';
import { LogPhotoResponse } from '../../../models/logs/photos/LogPhotoResponse';
import { EmailPreviewRequest } from '../../../models/email/EmailPreviewRequest';
import { AddReceivingLogRequest } from '../../../models/logs/AddReceivingLogRequest';
import { Option } from '../../../models/assets-data/receiving/ReceivingLogTableConfig.interface';
import { convertAzurePhotosToLogEntryPhotos, groupPhotosByKind, PhotoKindGroup, photoKindGroups, photoKindLabel } from '../../../shared/helpers/photos-utils';
import { LogEntryPhotoFile } from '../../../models/logs/photos/LogEntryPhotoFiles.interface';
import { downloadPhotoFiles } from '../../../helpers/download-photos';
import { User } from '../../../models/User.interface';
import { EmailPreviewResponse } from '../../../models/email/EmailPreviewResponse';
import { LogFilterParams } from '../../../models/logs/LogFilterParams';
import { ApiLogFilterParams } from '../../../models/logs/ApiLogFilterParams';
import { TableConfig } from '../../../models/table-config.interface';
import { itemsHaveExpeditingEmail } from '../../../domain/receiving-log/rules/damage-flags.rules';
import { SHARED_FLOW_ROUTE } from '../../../domain/receiving-log/rules/new-log-route.rules';
import { ReceivingLogApiService } from '../../../domain/receiving-log/api/receiving-log-api.service';
import { LogRowsEditResult } from '../../../models/logs/LogRowsEditResult';
import {
  LogFieldEditor,
  StructuredRowsState,
  diffLogRowFields,
  editorForField,
  isLogFormFieldLocked,
  lockedFieldReason,
  rowEditorChangeSummary,
  structuredRowsStateFrom,
} from '../../../domain/receiving-log/rules/log-row-form.rules';
import {
  employeeEmailChoices,
  employeeNameChoices,
} from '../../../domain/receiving-log/rules/employee-directory.rules';
import { ContactsService } from '../../../service/contact.service';

@Component({
  selector: 'app-receiving-log',
  templateUrl: './receiving-log.component.html',
  styleUrls: ['./receiving-log.component.css'],
})
export class ReceivingLogComponent implements OnInit, OnDestroy {
  // Subscriptions for various observables
  public logsLoadingSubscription!: Subscription;
  public photosLoadingSubscription!: Subscription;
  public isLargeScreenSubscription!: Subscription;
  private logSubscription!: Subscription;
  private selectedLocationSubscription!: Subscription;
  private logRowPhotosSubscription!: Subscription;
  private logRowDamagedPhotosSubscription!: Subscription;
  private userSubscription!: Subscription;
  private selectedLocationService: SelectedLocationService;

  // Services and utilities
  private _receivingLogService: ReceivingLogStateService;
  private _entraService: EntraService;
  private _assetsDataService: AssetsDataService;
  private _toastService: ToastService;
  private _screenSizeService: ScreenSizeService;
  private _photoService: PhotoService;
  private _emailService: EmailService;
  private _router: Router;
  private _formBuilder: FormBuilder = new FormBuilder();

  private _alertController: AlertController;

  // UI text and configuration data
  public toastText!: ToastText;
  public alertText!: AlertText;

  // Screen size and loading state
  public isLargeScreen!: boolean;
  // Phone-sized viewport (<= 768px) — mirrors the table's card breakpoint.
  // Used to declutter the crowded entry-details modal header on mobile.
  public isSmallScreen = false;
  public isSmallScreenSubscription!: Subscription;
  public logsLoading: DataLoading = { isLoading: false, message: '' };
  public photosLoading: DataLoading = { isLoading: false, message: '' };

  // User Data
  public user!: User;

  // Modal states
  public isFilterModalOpen = false;
  public isRowModalOpen = false;
  public isDateModalOpen = false;
  public isPhotoManagerModalOpen = false;
  public isEmailPreviewModalOpen = false;
  public isReprintModalOpen = false;
  /**
   * Which derived-field editor is open inside the entry-details modal, or null.
   *
   * Two separate editors, one per derived string, opened from a button on the
   * field each owns: containers by chips, items by PO line-item check-off. A user
   * changes one or the other, and each commits its own transaction.
   */
  public openFieldEditor: LogFieldEditor | null = null;
  // Forms for filtering and editing logs
  public filterForm!: FormGroup;
  public editLogForm!: FormGroup;

  // Table configuration and state
  public userIsAdmin: boolean = false;
  public columnData!: TableField[];
  public filterLogsLabelPlacement: string = 'stacked';
  public editLogLabelPlacement: string = 'stacked';
  public tableConfig!: TableConfig;

  // and Page Title
  public selectedLocation: SelectedLocation = {
    name: 'norfolk',
    label: 'Norfolk',
    laydownSpaceAvailable: false,
  };
  public pageTitle = this.selectedLocation.label + ' ' + 'Receiving Log';
  public archivedPageTitle =
    'Archived ' + this.selectedLocation.label + ' ' + 'Receiving Log';
  public currentPageTitle = this.pageTitle;

  // Date tracking for the date picker modal
  public tempSelectedDate: string | null = null;
  public selectedDate!: string;
  public selectedDateControl!: FormControl;

  // Log Filter/Search state
  public filterFormValues: LogFilterParams = new LogFilterParams();
  public filteredColumns: (keyof ReceivingLogRow)[] = [];
  public initialFilterColumnParams: { [key: string]: string | null } = {
    dateOut: null,
  };
  public filterColumnParams = this.initialFilterColumnParams; // Object to store filter values for each column
  public showArchivedLogs: boolean = false;
  public showLogsWithDateOut: boolean = false;
  public initialLogSearch = new ReceivingLogSearch({
    location: this.selectedLocation.name,
    filterColumnParams: { ...this.initialFilterColumnParams },
  });
  public logSearch: ReceivingLogSearch = this.initialLogSearch;

  //Fetching Logs and Pagination
  public receivingLogResponse: GetReceivingLogsResponse =
    new GetReceivingLogsResponse();
  public logCount!: number;
  public rowsPerPage: number = 200;
  public totalPageCount!: number;
  public receivingLogs!: ReceivingLogRow[];

  //Selected Row and Table Row Data
  public originalReceivingLogRow!: ReceivingLogRow;
  public selectedRow!: ReceivingLogRow;
  // The log row whose labels are being reprinted (drives the reprint modal).
  public reprintRow: ReceivingLogRow | null = null;
  public emailPhotos!: LogEntryPhoto[];
  public emailData!: EmailData;
  public logRowPhotos!: PhotoMetadata[];
  /**
   * The log's damage photos, which arrive in their own field of the photos
   * response. Held apart from `logRowPhotos` — they are stored separately and
   * must not reach the paths that delete or download a log's shipment photos.
   */
  public logRowDamagedPhotos: PhotoMetadata[] = [];
  /** Every photo split into one labelled grid per kind for the details panel. */
  public logRowPhotoGroups: PhotoKindGroup<PhotoMetadata>[] = [];
  public includePhotosInEmail: boolean = true;
  // True when the log being resent had outstanding/damaged items, i.e. an
  // expediting (missing-items) email accompanied the original notification.
  // Derived from the stored `items` string and surfaced in the preview.
  public selectedRowHasExpeditingEmail: boolean = false;

  /**
   * Whether the open entry has structured item/receipt rows — which decides
   * whether its `quantity` may be typed over on this form.
   *
   * `ReceivingLogRow` carries no such flag and the table row is all this modal is
   * built from, so the answer comes from
   * `GET /V1/receiving-log/{logId}/details` fired when the modal opens. Until it
   * lands (or if it never does) this stays `'unknown'`, which behaves like
   * `'structured'` — see `log-row-form.rules.ts` for why "could not tell" must
   * never license free-text editing of a derived string.
   */
  public structuredRows: StructuredRowsState = 'unknown';

  /**
   * A PAST-TENSE record of what the nested row editor changed, shown in the entry
   * modal after it closes.
   *
   * Not a pending badge: that editor commits in its own transaction, so by the
   * time this is populated the log has already moved. Its job is to make clear
   * that the entry on screen is no longer the one the user opened.
   */
  public rowEditorSummary: string[] = [];

  /** The §6.2.3 label warning carried out of the row editor, or null. */
  public rowEditorLabelWarning: string | null = null;

  /** Captions of the labels that row editor renumbered, as the stickers read them. */
  public rowEditorRenumberedCaptions: string[] = [];

  private _detailsSubscription?: Subscription;
  /** Keeps the Email / Logged By type-ahead lists fed from the cached directory. */
  private _employeesSubscription?: Subscription;
  /** Directory addresses, for the Email field's type-ahead. */
  public employeeEmailChoices: string[] = [];
  /** Directory names (preferred + last), for the Logged By field's type-ahead. */
  public employeeNameChoices: string[] = [];

  // Constructor with injected services and utilities
  // Fetches the UI configuration data and locations and initializes the table data
  constructor(
    receivingLogService: ReceivingLogStateService,
    entraService: EntraService,
    formBuilder: FormBuilder,
    assetsDataService: AssetsDataService,
    screenSizeService: ScreenSizeService,
    toastService: ToastService,
    router: Router,
    photoService: PhotoService,
    emailService: EmailService,
    alertController: AlertController,
    selectedLocationService: SelectedLocationService,
    private _logApi: ReceivingLogApiService,
    private _contactsService: ContactsService
  ) {
    this._receivingLogService = receivingLogService;
    this._entraService = entraService;
    this._toastService = toastService;
    this._formBuilder = formBuilder;
    this._router = router;
    this._photoService = photoService;
    this._screenSizeService = screenSizeService;
    this.isLargeScreen = this._screenSizeService.isLargeScreen.value;
    this._assetsDataService = assetsDataService;
    this._emailService = emailService;
    this._entraService = entraService;
    this._alertController = alertController;
    this.selectedLocationService = selectedLocationService;

    this.getUiConfig();
    this.initializeTableData();
  }

  //Manage Subscriptions and Fetch Logs on Init
  ngOnInit() {
    this._photoService.clearLogRowPhotos();
    // Unsubscribe from previous subscriptions if they exists
    if (this.isLargeScreenSubscription) {
      this.isLargeScreenSubscription.unsubscribe();
    }
    if (this.logSubscription) {
      this.logSubscription.unsubscribe();
    }
    if (this.selectedLocationSubscription) {
      this.selectedLocationSubscription.unsubscribe();
    }
    if (this.logsLoadingSubscription) {
      this.logsLoadingSubscription.unsubscribe();
    }
    if (this.logRowPhotosSubscription) {
      this.logRowPhotosSubscription.unsubscribe();
    }
    if (this.logRowDamagedPhotosSubscription) {
      this.logRowDamagedPhotosSubscription.unsubscribe();
    }
    if (this.photosLoadingSubscription) {
      this.photosLoadingSubscription.unsubscribe();
    }

    // The Email and Logged By fields on the row-details form type-ahead against
    // the directory. Fetched once per app session, so this is normally a cache
    // read; the subscription is what fills the lists if it lands while the page
    // is already up.
    this._employeesSubscription?.unsubscribe();
    this._employeesSubscription = this._contactsService.fetchedEmployees.subscribe(() => {
      this.applyEmployeeChoices();
    });
    // The service toasts the failure itself; this handler just keeps a failed
    // fetch from surfacing as an unhandled rejection.
    this._contactsService.ensureCompanyEmails().subscribe({
      error: (err) => console.error('Employee directory unavailable:', err),
    });

    // Subscribe to screen size changes
    this.isLargeScreenSubscription =
      this._screenSizeService.isLargeScreen.subscribe(
        (isLargeScreen: boolean) => {
          this.isLargeScreen = isLargeScreen;
        }
      );

    this.isSmallScreenSubscription =
      this._screenSizeService.isSmallScreen.subscribe(
        (isSmallScreen: boolean) => {
          this.isSmallScreen = isSmallScreen;
        }
      );

    // Subscribe to data loading state changes
    this.logsLoadingSubscription =
      this._receivingLogService.dataLoading.subscribe(
        (dataLoading: DataLoading) => {
          this.logsLoading = dataLoading;
        }
      );

    this.photosLoadingSubscription = this._photoService.dataLoading.subscribe(
      (dataLoading: DataLoading) => {
        this.photosLoading = dataLoading;
      }
    );

    this.logRowPhotosSubscription = this._photoService.logRowPhotos$.subscribe(
      (photos: PhotoMetadata[]) => {
        // The flat list stays grouped by kind for the paths that take all of them
        // at once (download, email); the details panel renders the groups.
        this.logRowPhotos = groupPhotosByKind(photos);
        this.rebuildPhotoGroups();
      }
    );

    this.logRowDamagedPhotosSubscription = this._photoService.logRowDamagedPhotos$.subscribe(
      (photos: PhotoMetadata[]) => {
        this.logRowDamagedPhotos = photos;
        this.rebuildPhotoGroups();
      }
    );

    // Subscribe to fetched log updates and update log data and pagination
    this.logSubscription = this._receivingLogService.fetchedLogs.subscribe(
      (logs: GetReceivingLogsResponse) => {
        this.logCount = logs.totalRowCount;
        this.totalPageCount = Math.ceil(this.logCount / this.rowsPerPage);
        this.receivingLogs = logs.logList.map(
          (log: any) => new ReceivingLogRow(log)
        );
      }
    );

    // Subscribe to location updates, fetch logs accordingly, and update page title
    this.selectedLocationSubscription =
      this.selectedLocationService.selectedLocation$.subscribe(
        (location: SelectedLocation) => {
          this.selectedLocation = location;
          this.initialLogSearch.location = location.label;
          const pageTitle = location.label + ' ' + 'Receiving Log';
          this.pageTitle = pageTitle;
          this.archivedPageTitle = 'Archived ' + pageTitle;
          this.currentPageTitle = pageTitle;
          this.tableConfig.title = pageTitle;

          const logSearch = this.logSearch;
          logSearch.location = location.label;
          this._receivingLogService.fetchLogs(logSearch);
        }
      );

    this.userSubscription = this._entraService.user.subscribe((user: User) => {
      this.user = user;
    });
  }

  /** Every photo on the log, shipment/slip plus damage — what "all photos" means. */
  public get totalLogRowPhotoCount(): number {
    return (this.logRowPhotos?.length ?? 0) + this.logRowDamagedPhotos.length;
  }

  /**
   * Rebuilds the details panel's labelled photo grids from the two streams that
   * feed them — the log's shipment/slip photos and its damage photos, which the
   * API returns in separate fields.
   *
   * Damage photos become one group of their own, labelled from the kind rather
   * than from whatever each row carries, so they stay together and stay named
   * even for a row the backend sent without a kind. Built here rather than in the
   * template so *ngFor is not handed a freshly built array on every
   * change-detection pass.
   */
  private rebuildPhotoGroups(): void {
    const groups = photoKindGroups(this.logRowPhotos ?? []);
    if (this.logRowDamagedPhotos.length > 0) {
      groups.push({
        kind: 'Damage',
        label: photoKindLabel('Damage'),
        photos: this.logRowDamagedPhotos,
      });
    }
    this.logRowPhotoGroups = groups;
  }

  /**
   * Cleanup subscriptions when the component is destroyed
   */
  ngOnDestroy() {
    this.logSubscription.unsubscribe();
    this.selectedLocationSubscription.unsubscribe();
    this.logsLoadingSubscription.unsubscribe();
    this.isLargeScreenSubscription.unsubscribe();
    this.isSmallScreenSubscription?.unsubscribe();
    this.logRowPhotosSubscription.unsubscribe();
    this.logRowDamagedPhotosSubscription?.unsubscribe();
    this.photosLoadingSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    this._detailsSubscription?.unsubscribe();
    this._employeesSubscription?.unsubscribe();
  }

  /**
   * Rebuilds the type-ahead lists for the Email and Logged By fields from the
   * cached employee directory.
   *
   * Both stay text inputs with an autocomplete list beside them. A true select
   * renders an element per option up front, and the directory rendered twice over
   * is enough to freeze the details modal on open — so the list is only ever
   * materialized a few rows at a time, as the user types.
   */
  public applyEmployeeChoices(): void {
    const employees = this._contactsService.fetchedEmployees.value;
    this.employeeEmailChoices = employeeEmailChoices(employees);
    this.employeeNameChoices = employeeNameChoices(employees);
  }

  /**
   * The type-ahead list for a field, or null for a field that isn't one.
   *
   * Returns the STORED array rather than building one, because this is called
   * from a template binding: a fresh array each change-detection pass would
   * re-trigger the dropdown's ngOnChanges on every cycle.
   *
   * @param column - The field being rendered.
   */
  /**
   * The field the user is currently in, or null. Only that field's type-ahead may
   * open — see the dropdown's `active` input for why this has to be explicit.
   */
  public activeFieldName: string | null = null;

  /** Opens the field's type-ahead, if it has one. */
  public onFieldFocus(column: TableField): void {
    this.activeFieldName = column.name;
  }

  /**
   * Closes it again. Picking an option does not run this: the dropdown's rows
   * cancel their mousedown, so the input never loses focus to them.
   */
  public onFieldBlur(column: TableField): void {
    if (this.activeFieldName === column.name) { this.activeFieldName = null; }
  }

  public employeeChoicesFor(column: TableField): string[] | null {
    if (column.name === 'email') { return this.employeeEmailChoices; }
    if (column.name === 'loggedBy') { return this.employeeNameChoices; }
    return null;
  }

  /**
   * Initializes table data with assets configuration and sets up filtering form
   *  This method is called on initialization
   */
  public initializeTableData() {
    this.userIsAdmin = this._entraService.user.value.isAdmin;
    const tableData = this._assetsDataService.GetReceivingLogTableData();
    const columnData = tableData.inputs;

    const storageFacilityField = columnData.find(field => field.name === 'storageFacility');
      if (storageFacilityField) {
        const allLocations = this._assetsDataService.GetLocationData();
        const locationOptions: Option[] = allLocations.map(location => ({
          label: location.locationLabel,
          value: location.locationLabel
        }));
        storageFacilityField.options = locationOptions;
      }


    this.columnData = columnData;
    const filterForm = this.createFilterFormGroup(
      columnData,
      this._formBuilder
    );
    this.filterForm = filterForm;
  }

  /**
   * Fetches and sets UI configuration data from the assets data service.
   * This method is called on initialization
   */
  public getUiConfig(): void {
    const uiConfigData = this._assetsDataService.GetUiConfigData();
    const labelPlacement = uiConfigData.labelPlacement;
    this.filterLogsLabelPlacement = labelPlacement.filterLogs;
    this.editLogLabelPlacement = labelPlacement.editLog;
    this.toastText = uiConfigData.toastText;
    this.alertText = uiConfigData.alertText;

    const pageTitle = this.selectedLocation.label + ' ' + this.pageTitle;
    this.pageTitle = pageTitle;
    this.archivedPageTitle =
      'Archived ' + this.selectedLocation.label + ' ' + this.pageTitle;

    this.tableConfig = {
      title: pageTitle,
      notes: {
        noLogs: 'No logs found for the selected filters.',
      },
      buttons: {
        filterButton: 'Filter',
        newEntryButton: 'New Entry',
        deleteEntryButton: 'Delete Entry',
        generateEmailButton: 'Generate Email from Log Entry',
        reprintLabelsButton: 'Reprint Labels for Log Entry',
        viewLogsButton: 'View Receiving Logs',
      },
      hasEmailFeature: true,
      hasReprintFeature: true,
    };
  }


  

  /**
   * Navigates to the "New Log" page — the shared receiving flow, on every device,
   * same as the side menu and the landing page's entry button.
   * This method is called when the user clicks the "New Entry" button.
   */
  public onNavToNewLogPage() {
    this._router.navigate([SHARED_FLOW_ROUTE]);
  }

  //Table Action Button Methods ---------------------------------------------------------------------

  //Fetch photos for the selected row
  getPhotosForSelectedRow(logId: number, onSuccessFunction?: Function) {
    //onSuccessFunction

    this._photoService.getPhotosForLogEntry(
      logId.toString(),
      onSuccessFunction
    );
  }

  /**
   * Downloads every photo attached to the currently-viewed log entry.
   * Fetches the raw file bytes (base64) and saves each as an individual file.
   */
  public downloadAllPhotos(): void {
    const logId = this.selectedRow?.id;
    if (!logId) return;

    // Damage photos count towards "has photos": a log can hold nothing but damage
    // photos, and refusing to download those would be wrong.
    if (this.totalLogRowPhotoCount === 0) {
      this._toastService.showWarningMessage('No photos to download for this log entry.');
      return;
    }

    this._photoService.getPhotoFilesForLogEntry(logId, false, (files: LogEntryPhotoFile[]) => {
      downloadPhotoFiles(files);
    });
  }

  /**
   * Deletes the selected log entry.
   * - Asks for confirmation before deleting.
   * - Calls the service to delete the log by ID.
   * - Refreshes the logs after successful deletion.
   */
  public onDeleteLogRow(logId: number): void {
    // Confirm before deleting
    const confirmDelete = confirm(
      'Are you sure you want to delete this log entry?'
    );
    if (!confirmDelete) return;

    const onSuccessFunction = () => {
      this._receivingLogService.fetchLogs(this.logSearch); // Refresh the logs
    };

    this._receivingLogService.deleteLog(logId, onSuccessFunction);
  }

  /**
   * Restores an archived log entry back to the active table.
   * - Asks for confirmation before restoring.
   * - Calls the service to move the log from archive by ID.
   * - Refreshes the logs after successful restoration.
   */
  public onRestoreLogRow(archiveRowId: number): void {
    // Confirm before restoring
    const confirmRestore = confirm(
      'Are you sure you want to restore this log entry from the archive?'
    );
    if (!confirmRestore) return;

    const onSuccessFunction = () => {
      this._receivingLogService.fetchLogs(this.logSearch); // Refresh the logs
    };

    this._receivingLogService.moveLogFromArchive(
      archiveRowId,
      onSuccessFunction
    );
  }

  /**
   * Generates an email from the selected log entry.
   * First validates recipients and handles confirmations, then processes photos and email generation.
   *
   * @param logRow The log row data to generate an email for
   */
  public generateEmailFromLogRow(logRow: ReceivingLogRow): void {
    if (!logRow || !logRow.id) {
      this._toastService.showWarningMessage(
        'Cannot generate email: Invalid log data'
      );
      return;
    }

    // Detect whether this log carried missing/damaged items — the condition that
    // originally triggered a separate expediting email — so the preview can flag it.
    this.selectedRowHasExpeditingEmail = itemsHaveExpeditingEmail(logRow.items);

    const locations = this._assetsDataService.GetLocationData();
    const location = locations.find(
      (loc: Location) => loc.locationLabel === logRow.storageFacility
    );

    if (!location) {
      this._toastService.showWarningMessage(
        'Cannot generate email: Location information missing'
      );
      return;
    }

    // Basic email data

    const logType = logRow.jcDept?.toLowerCase().includes('sv')
      ? 'Service'
      : 'Project';
    const userEmail = this._entraService.user.value.email;
    const locationEmails =
      logType === 'Service' ? location.serviceEmails : location.projectEmails;
    const existingRecipients = logRow.email
      ? logRow.email
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email !== '')
      : [];
    // Parse existing recipients from log
    const emailData = new EmailData({
      recipients: existingRecipients,
      logType: logType,
      projectEmails: location.projectEmails,
      serviceEmails: location.serviceEmails,
      address: location.address,
      userEmail: userEmail,
      includePhotosInEmail: true,
    });

    if (existingRecipients.length > 0) {
      this.fetchEmailPreview(emailData, logRow);
    } else {
      const continueFunction = () => {
        emailData.recipients = [userEmail];
        this.fetchEmailPreview(emailData, logRow);
      };
      this.showConfirmationAlert(
        this.alertText.noRecipients.title,
        this.alertText.noRecipients.message,
        'Cancel',
        'Continue',
        continueFunction
      );
    }
  }

  /**
   * Opens the reprint-labels modal for a log row. Available to all users (like
   * the email action). The modal configures the print (type / qty / printer) and
   * submits to `POST /V1/print/from-log/{logId}`.
   *
   * @param logRow The log row to reprint labels for.
   */
  public onReprintLogRow(logRow: ReceivingLogRow): void {
    if (!logRow || !logRow.id) {
      this._toastService.showWarningMessage('Cannot reprint labels: Invalid log data');
      return;
    }
    this.reprintRow = logRow;
    this.isReprintModalOpen = true;
  }

  /** Closes the reprint-labels modal. */
  public onCloseReprintModal(): void {
    this.isReprintModalOpen = false;
  }

  // message: 'There are no recipients specified for this email or location. You can either add an email to the log row via updating the log row, or add yourself as the recipient.',
  // message: `This email only has ${locationEmails} as a recipient. You can either go into the log and update it with an email recipient, or continue.`,

  public fetchEmailPreview(
    emailData: EmailData,
    logRow: ReceivingLogRow
  ): void {
    this.emailData = emailData;

    if (!logRow.id) {
      return;
    }
    this._receivingLogService.setLogRowData(logRow);
    this.selectedRow = logRow;

    // Define success handler for photo fetch
    const onPhotoSuccessFunction = (logRowPhotos: PhotoMetadata[]) => {
      // Update the log entry photos that will appear in the email preview modal
      const logEntryPhotos = convertAzurePhotosToLogEntryPhotos(logRowPhotos);
      this._photoService.updateLogEntryPhotos(logEntryPhotos);

      // Generate email preview and open modal
      const formattedLogRow = new ApiReceivingLogRow(logRow);
      const emailPreviewRequest = new EmailPreviewRequest(
        formattedLogRow,
        emailData
      );

      this._emailService.generateEmailPreview(emailPreviewRequest);
      this.isEmailPreviewModalOpen = true;
    };

    // Fetch photos for the log entry
    this.getPhotosForSelectedRow(logRow.id, onPhotoSuccessFunction);
  }

  /**


/**
 * Shows a confirmation dialog with customizable buttons and message
 * 
 * @param header Dialog header text
 * @param message Dialog message text
 * @param cancelText Text for the cancel button
 * @param continueText Text for the continue button
 * @param onContinue Function to call when user clicks continue
 */
  private showConfirmationAlert(
    header: string,
    message: string,
    cancelText: string,
    continueText: string,
    onContinue: () => void
  ): void {
    this._alertController
      .create({
        header: header,
        message: message,
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
          },
          {
            text: continueText,
            handler: () => {
              onContinue();
            },
          },
        ],
      })
      .then((alert) => alert.present());
  }

  /**
   * Closes the email preview modal
   */
  public onCloseEmailPreviewModal(): void {
    this.isEmailPreviewModalOpen = false;
  }

  /**
   * Handles toggle of photo inclusion in email
   */
  public onTogglePhotosInEmail(event: any): void {
    this.includePhotosInEmail = event.detail.checked;
    console.log('onTogglePhotosInEmail', event.detail.checked);
    const emailData = {
      ...this.emailData,
      includePhotosInEmail: event.detail.checked,
    };
    this.emailData = emailData;
  }

  /**
   * Sends an email with photos from Azure storage
   * This method is called when the user clicks the "Send Email" button within the email preview modal
   * If the user has selected to include photos in the email, the photos are sent from Azure storage
   * If the user has not selected to include photos in the email, an empty array is sent
   */
  onSendEmail() {
    console.log('onSendEmail');
    console.log('this.emailData on send email', this.emailData);
    const formattedLogRow = new ApiReceivingLogRow(this.selectedRow);
    const photoBlobNames = this.logRowPhotos.map((photo) => photo.blobName);
    let receivingLogRequest: AddReceivingLogRequest;
    if (this.emailData.includePhotosInEmail) {
      receivingLogRequest = new AddReceivingLogRequest(
        formattedLogRow,
        this.emailData,
        photoBlobNames
      );
    } else {
      receivingLogRequest = new AddReceivingLogRequest(
        formattedLogRow,
        this.emailData
      );
    }

    const onSuccessFunction = () => {
      this.isEmailPreviewModalOpen = false;
      this.includePhotosInEmail = true;
    };
    this._emailService.sendEmailWithAzurePhotos(
      receivingLogRequest,
      onSuccessFunction
    );
  }

  //DatePicker Modal Methods ---------------------------------------------------------------------

  /**
   * Opens the date picker modal for a form field and prepares a temporary date value
   * This method is called when the user clicks into a date field
   * @param formFieldName - The name of the form field
   * @param formGroup - The FormGroup containing the field
   */
  onOpenDateModal(formFieldName: string, formGroup: FormGroup) {
    const dateControl = formGroup.get(formFieldName);

    if (dateControl) {
      this.selectedDateControl = dateControl as FormControl;

      if (dateControl.value === null) {
        this.tempSelectedDate = new Date().toISOString();
      } else {
        this.tempSelectedDate = new Date(dateControl.value).toISOString();
      }

      this.isDateModalOpen = true;
    }
  }

  /**
   * Updates the selected date from the date picker modal
   * This method is passed to the date picker component as an output
   * @param date - The selected date string
   */
  public updateSelectedDate(date: string) {
    if (date !== null) this.tempSelectedDate = new Date(date).toISOString();
  }

  /**
   * Closes the date picker modal and saves the selected date to the form field
   * This method is called when the user minimizes the date picker modal and is also
   * passed to the date picker component as an output
   */
  closeDateModal() {
    if (this.tempSelectedDate !== null) {
      // Save the selected date on clickaway
      const selectedDate = formatDateString(this.tempSelectedDate);
      this.selectedDateControl.setValue(selectedDate);
    }
    this.isDateModalOpen = false;
  }

  /**
   * Cancels date selection and closes the modal
   * This method is passed to the date picker component as an output
   */
  cancelDateSelection() {
    this.tempSelectedDate = null;
    this.isDateModalOpen = false;
  }

  //View / Update / Delete Log Methods ---------------------------------------------------------------------

  /**
   * Creates a FormGroup for editing a log entry.
   * This method is called when the user opens the row details modal.
   *
   * @param data - Array of field metadata defining the form structure.
   * @param row - The row data being edited.
   * @param fb - The FormBuilder instance.
   * @returns A FormGroup containing form controls for the log fields.
   */
  public createEditLogFormGroup(
    data: any[],
    row: ReceivingLogRow,
    fb: FormBuilder
  ): FormGroup {
    const formControls: { [key: string]: FormControl } = {};

    data.forEach((field) => {
      // Retrieve the initial value from the row data
      let startingValue = row[field.name as keyof ReceivingLogRow];
      // Set validators if the field is required
      const validators = field.required ? [Validators.required] : [];

      // Set the control with value, validators, and disabled state
      const controlOptions = {
        value: startingValue,
        disabled: field.disabled && !this.userIsAdmin ? true : false,
      };
      formControls[field.name] = fb.control(controlOptions, validators);
    });

    return fb.group(formControls);
  }

  /**
   * Opens the row details modal when a user clicks a row.
   * This method initializes the form for editing the selected row.
   *
   * @param item - The selected row item.
   */
  onOpenRowModal(item: ReceivingLogRow) {
    this.selectedRow = item;

    // Fetch photos for the selected log entry
    if (item.id) {
      this.getPhotosForSelectedRow(item.id);
      this._photoService.clearLogEntryPhotos();
    }

    // A fresh open shows nothing about a previous entry's row edit.
    this.rowEditorSummary = [];
    this.rowEditorLabelWarning = null;
    this.rowEditorRenumberedCaptions = [];
    this.structuredRows = 'unknown';
    this.loadStructuredRowsFlag(item.id);

    this.isRowModalOpen = true;
    const filterFormBuilder = new FormBuilder();
    // Initialize the form for editing the selected row
    this.editLogForm = this.createEditLogFormGroup(
      this.columnData,
      item,
      filterFormBuilder
    );

    // Set initial form control states
    this.updateFormControlStates();
  }

  /**
   * Asks the details endpoint whether this log has structured rows, which is what
   * decides whether its `quantity` may be typed over on the form below.
   *
   * ONE extra read per modal open, and it buys two things: the flag itself, which
   * exists nowhere on the list payload, and a fresh answer at the moment the user
   * starts editing rather than one that has been sitting in the table since the
   * page loaded. The alternative — a flag on the list payload — would be cheaper
   * per open but needs backend work and is not in the frozen contract.
   *
   * A failure deliberately leaves `structuredRows` at `'unknown'`, which locks the
   * quantity. Unlocking on "could not tell" is the dangerous direction: `legacy`
   * is a positive claim that licenses free-text editing of a derived string, and
   * the details payload takes the same position about its own soft-failures
   * (handoff §1.1).
   */
  private loadStructuredRowsFlag(logId: number | undefined): void {
    this._detailsSubscription?.unsubscribe();
    if (!logId) { return; }

    this._detailsSubscription = this._logApi.getReceivingLogDetails(logId).subscribe({
      next: (details) => {
        this.structuredRows = structuredRowsStateFrom(details);
        // The lock is applied by updateFormControlStates, which already ran with
        // the answer still unknown — re-run it now that it is known.
        this.updateFormControlStates();
      },
      error: (err) => {
        console.error('Could not read whether this log has structured rows:', err);
        this.structuredRows = 'unknown';
      },
    });
  }

  // Close the view-row-details modal
  closeRowModal() {
    this.isRowModalOpen = false;
    this.isPhotoManagerModalOpen = false;
    this.openFieldEditor = null;
    this._detailsSubscription?.unsubscribe();
  }

  onOpenPhotoManager() {
    this.isPhotoManagerModalOpen = true;
  }

  onClosePhotoManager() {
    this.isPhotoManagerModalOpen = false;
    if (this.selectedRow.id) {
      this.getPhotosForSelectedRow(this.selectedRow.id);
    }
  }

  /**
   * Retrieves the values from the edit log form and maps them to a ReceivingLogRow object.
   * This method is called within the updateLogRow method.
   * @returns The updated ReceivingLogRow object.
   */
  public getEditLogFormValues(): ReceivingLogRow {
    const formControls = this.editLogForm.controls; // Access form controls
    const editLogData: { [key: string]: any } = {}; // Initialize an object to hold form values

    // Extract values from form controls
    Object.keys(formControls).forEach((key) => {
      editLogData[key as keyof ReceivingLogRow] = formControls[key].value;
    });

    // Return a new ReceivingLogRow object with updated values
    const receivingLogData = new ReceivingLogRow(editLogData);
    return receivingLogData;
  }

  /**
   * Updates the log row if there are changes in the form.
   * - Compares form values to the selected row.
   * - If changes exist, sends the update request.
   * - If no changes, displays a warning.
   *
   * This method is called when the user submits the edit log form.
   */
  public updateLogRow() {
    if (this.editLogForm.valid) {
      const editLogData = this.getEditLogFormValues(); // Get updated data from the form

      // The diff lives in `log-row-form.rules.ts`, and the two things it has to
      // get right are both silent when wrong:
      //
      //   * a LOCKED field is dropped even when its value differs. Angular keeps
      //     a `.value` on a disabled control and getEditLogFormValues() reads
      //     `.value` from every control, so rendering the quantity read-only does
      //     NOT by itself keep it out of this request.
      //   * `selectedRow` is the BASELINE, so it must be current. The nested row
      //     editor commits on its own, and after it does, this row is historical
      //     — see onRowEditorClosed.
      const updateLogRowValues: any = diffLogRowFields(
        editLogData as unknown as Record<string, unknown>,
        this.selectedRow as unknown as Record<string, unknown>,
        this.columnData.map((column) => column.name),
        this.structuredRows,
      );

      // If there are any changes, proceed with the update
      if (Object.keys(updateLogRowValues).length > 0) {
        updateLogRowValues.id = this.selectedRow.id; // Add the id to the object
        updateLogRowValues.poNumber = this.selectedRow.poNumber; // Add the poNumber to the object
        const onSuccessFunction = () => {
          this.editLogForm.reset(); // Reset the form after successful update
          this.isRowModalOpen = false; // Close the modal
          this._receivingLogService.fetchLogs(this.logSearch); // Fetch the logs again to update the table
        };

        // Call the service to update the log with only the changed values
        this._receivingLogService.updateLog(
          updateLogRowValues,
          onSuccessFunction
        );
      } else {
        // If no changes, show a warning
        this._toastService.showWarningMessage(
          this.toastText.logEntry.update.noChanges
        );
      }
    } else {
      // Handle form validation errors
      this.editLogForm.markAllAsTouched();
      this._toastService.showWarningMessage(
        this.toastText.logEntry.update.invalidFormError
      );
    }
  }

  /**
   * Opens the structured row editor as a modal NESTED inside this one —
   * handoff-18's replacement for typing over the `items` and `quantity` text on
   * the form behind this button.
   *
   * That editor works on the receipt and item ROWS and lets the backend re-derive
   * `quantity`, so the strings can't end up contradicting the receipt ledger
   * (which drives the PO screen) or the item rows (which are what printed on the
   * stickers).
   *
   * This modal stays OPEN behind it. A modal that navigates to a route is
   * disorienting, and the user comes straight back here when the editor closes —
   * which is also where the re-baselining has to happen.
   *
   * Refuses on an archived log: `MM.ReceivingLogArchive` reassigns ids, so the id
   * in this row would address a different log entirely.
   */
  public openFieldEditorFor(editor: LogFieldEditor | null): void {
    if (!editor || this.showArchivedLogs || !this.selectedRow?.id) { return; }
    this.openFieldEditor = editor;
  }

  /** The editor a column's button opens, or null when the column is plain free text. */
  public editorFor(column: TableField): LogFieldEditor | null {
    return editorForField(column.name, this.structuredRows);
  }

  /**
   * Takes the row editor's result and makes this modal tell the truth again.
   *
   * ⚠️ THE HAZARD THIS CLOSES, stated exactly — it is narrower than "the diff
   * sees a difference", and the fix has to match the mechanism.
   *
   * This modal snapshots the row into `selectedRow` AND into `editLogForm` when
   * it opens. The row editor then commits in its own transaction, and neither
   * snapshot moves. So a user who typed into the `items` textarea BEFORE opening
   * the editor — which this modal fully allows, since `items` stays editable —
   * now has a form value that differs from the baseline, and both of them
   * describe rows the server has already replaced. "Update Entry" resolves that
   * difference by sending the PRE-EDIT text, straight over the note the row
   * editor just derived. On a legacy log the same thing happens to `quantity`,
   * where the Gap-3 lock deliberately does not apply.
   *
   * So this re-BASELINES rather than merely annotating, and refreshes BOTH
   * snapshots: `selectedRow` so the baseline is current, and the two controls so
   * the form is. Refreshing only the row is not a fix — the form value would
   * still differ from the new baseline and still be sent.
   *
   * Refreshing the `items` control discards what the user had typed. That is the
   * intended trade: the text described rows that no longer exist, and the
   * recomposed note is right there for them to re-add to. Only those two controls
   * are patched — the whole form is not rebuilt — so typing in any other field
   * survives.
   */
  public onRowEditorClosed(result: LogRowsEditResult | null): void {
    this.openFieldEditor = null;
    if (!result || !this.selectedRow) { return; }

    const before = { quantity: this.selectedRow.quantity, items: this.selectedRow.items };

    this.selectedRow.quantity = result.quantity;
    this.selectedRow.items = result.items;
    this.editLogForm?.get('quantity')?.setValue(result.quantity);
    this.editLogForm?.get('items')?.setValue(result.items);

    // Past tense: this already happened, in its own transaction. It is not
    // waiting on the Update button.
    this.rowEditorSummary = rowEditorChangeSummary(before, result);
    this.rowEditorLabelWarning = result.renumberWarning;
    this.rowEditorRenumberedCaptions = result.renumberedCaptions;

    // The editor's own read told us this log has rows; nothing about that changed.
    if (this.structuredRows === 'unknown') { this.structuredRows = 'structured'; }
    this.updateFormControlStates();
  }

  /** Whether a form field is locked because the row editor owns it (Gap 3). */
  public isFieldLockedByRows(column: TableField): boolean {
    return isLogFormFieldLocked(column.name, this.structuredRows);
  }

  /** Why a field is locked, in the user's terms — or null when it is editable. */
  public lockedFieldHint(column: TableField): string | null {
    return lockedFieldReason(column.name, this.structuredRows);
  }

  /**
   * Opens a new window displaying row details in a readable format.
   */
  public openRowDetailsWindow() {
    // Escape function to prevent XSS
    const escapeHTML = (str: string) => {
      return str.replace(
        /[&<>"']/g,
        (match) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          }[match] || match)
      );
    };

    // Generate table rows dynamically based on column data
    let tableRows = this.columnData
      .map(
        (column: TableField) => `
        <tr>
          <td><strong>${escapeHTML(column.label)}:</strong></td>
          <td>${escapeHTML(
            this.selectedRow[
              column.name as keyof ReceivingLogRow
            ]?.toString() || 'N/A'
          )}</td>
        </tr>
      `
      )
      .join('');

    // Construct the new window content
    const innerHTML = `
        <html>
          <head>
            <title>Log Details</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h2 { color: #333; }
              table { width: 100%; border-collapse: collapse; }
              td { padding: 8px; border-bottom: 1px solid #ddd; }
            </style>
          </head>
          <body>
            <h2>${this.selectedLocation.label} Receiving Log Entry Details</h2>
            <table>${tableRows}</table>
          </body>
        </html>
      `;

    // Open a new window and insert the generated content
    const newWindow = window.open('', '_blank', 'width=600,height=400');
    if (!newWindow) {
      console.error('Failed to open the new window. This might be blocked.');
      this._toastService.showWarningMessage(this.toastText.window.popoutError);
      return;
    }

    if (newWindow) {
      // Your document write logic here
      newWindow.document.open();
      newWindow.document.write(innerHTML);
      newWindow.document.close();
    }
  }

  //Filtering/Sorting Methods ---------------------------------------------------------------------

  /**
   * Resets all filters to their initial state.
   */
  public onClearFilters() {
    this.filterForm.reset(); // Clear the form inputs
    this.showLogsWithDateOut = false;
    this.showArchivedLogs = false;
    this.logSearch = this.initialLogSearch; // Restore initial search parameters
    this.updateFilters(this.initialFilterColumnParams); // Apply initial filters
  }

  /**
   * Creates a FormGroup for filtering logs based on user-selected criteria.
   * There is an added '_ALL' field that allows for cross-column search.
   *
   * @param data - Array of field metadata defining the filter structure.
   * @param fb - The FormBuilder instance.
   * @returns A FormGroup for the filter form.
   */
  public createFilterFormGroup(data: any[], fb: FormBuilder): FormGroup {
    const formControls: { [key: string]: FormControl } = {};

    data.forEach((field) => {
      const startingValue =
        this.filterFormValues[field.name as keyof LogFilterParams];
      formControls[field.name] = fb.control(startingValue); // Default value is empty string
    });
    formControls['_ALL'] = fb.control(this.filterFormValues['_ALL']);
    return fb.group(formControls);
  }

  /** Opens the filter modal. */
  public onOpenFilterModal() {
    this.isFilterModalOpen = true;
  }

  /** Closes the filter modal. */
  public closeFilterModal() {
    this.isFilterModalOpen = false;
  }

  /** Toggles the visibility of archived logs and updates the page title accordingly.
   * If showArchivedLogs is true and showLogsWithDateOut is false, showLogsWithDateOut is set to true.
   */
  public onToggleArchivedLogs() {
    const updatedShowArchivedLogs = !this.showArchivedLogs;
    if (updatedShowArchivedLogs && !this.showLogsWithDateOut) {
      this.showLogsWithDateOut = true;
    }
    this.showArchivedLogs = updatedShowArchivedLogs;

    if (updatedShowArchivedLogs) {
      this.currentPageTitle = this.archivedPageTitle;
      this.tableConfig.title = this.archivedPageTitle;
    } else {
      this.currentPageTitle = this.pageTitle;
      this.tableConfig.title = this.pageTitle;
    }

    // Update form control states if the edit form is open
    if (this.editLogForm) {
      this.updateFormControlStates();
    }
  }

  /**
   * Updates the enabled/disabled state of form controls based on current settings
   */
  private updateFormControlStates() {
    if (!this.editLogForm) return;

    this.columnData.forEach((column) => {
      const control = this.editLogForm.get(column.name);
      if (control) {
        if (this.shouldDisableEditField(column)) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });
  }

  /** Toggles the display of logs that have a 'dateOut' value.
   * If showLogsWithDateOut is false, showArchivedLogs is set to false and the dateOut filter is cleared.
   */
  public onToggleLogsWithDateOut() {
    const updatedShowLogsWithDateOut = !this.showLogsWithDateOut;
    if (!updatedShowLogsWithDateOut) {
      this.showArchivedLogs = false;
      this.filterForm.controls['dateOut'].setValue(null);
    }
    this.showLogsWithDateOut = !this.showLogsWithDateOut;
  }

  /**
   * Extracts and transforms filter values from the form for API submission.
   *
   * This method handles two distinct types of search parameters:
   * 1. Standard field filters corresponding to ReceivingLogRow properties
   * 2. Cross-column search via the "_ALL" parameter
   *
   * The implementation specifically addresses date formatting challenges by using
   * model conversion (form values → ReceivingLogRow → ApiReceivingLogRow) while
   * ensuring the special "_ALL" parameter is preserved throughout the process.
   *
   * @returns ApiLogFilterParams ready for API consumption with properly formatted dates
   */
  public getFilterValuesFromForm(): ApiLogFilterParams {
    // Capture the cross-column search value separately
    const generalSearch = this.filterForm.controls['_ALL'].value;

    // Initialize container for standard fields (excluding _ALL)
    const standardFieldValues: { [key: string]: any } = {};

    // Extract standard field values
    Object.keys(this.filterForm.controls)
      .filter((key) => key !== '_ALL')
      .forEach((key) => {
        standardFieldValues[key] = this.filterForm.controls[key].value;

        // Update date filter flag if needed
        if (key === 'dateOut' && this.filterForm.controls[key].value) {
          this.showLogsWithDateOut = true;
        }
      });

    // Process standard fields through model conversion chain for proper date formatting
    const receivingRowData = new ReceivingLogRow(standardFieldValues);
    const apiReceivingData = new ApiReceivingLogRow(receivingRowData);

    // Create API params from converted model
    const apiLogFilterParams = new ApiLogFilterParams(apiReceivingData);
    // Create client-side filter values from converted model
    this.filterFormValues = new LogFilterParams(receivingRowData);

    // Add cross-column search parameter if provided
    if (generalSearch) {
      apiLogFilterParams['_ALL'] = generalSearch;
      this.filterFormValues['_ALL'] = generalSearch;
    }

    return apiLogFilterParams;
  }

  /**
   * Applies user-defined filters to the receiving log search.
   *
   * This method processes filter selections from the modal form and transforms them
   * into properly formatted search parameters for the API. It handles several special cases:
   *
   * 1. Archive toggle: Controls whether archived logs are included in search results
   * 2. DateOut handling: Special logic for including/excluding logs without checkout dates
   * 3. Cross-column search: Processes the "_ALL" parameter for searching across all columns
   *
   * The method collects form values, transforms them into appropriate filter parameters,
   * and then updates the search configuration before triggering a new search.
   */
  public onSubmitFilters() {
    // Close the filter modal UI
    this.closeFilterModal();

    // Update archive search flag based on user selection
    this.logSearch.searchArchiveTable = this.showArchivedLogs;

    // Handle special DateOut parameter based on checkbox selection
    // If showLogsWithDateOut is true: include all logs regardless of DateOut value
    // If showLogsWithDateOut is false: only show logs where DateOut is null (items not checked out)
    this.logSearch.filterColumnParams = this.logSearch.filterColumnParams || {};
    if (this.showLogsWithDateOut) {
      delete this.logSearch.filterColumnParams['dateOut'];
    } else {
      this.logSearch.filterColumnParams['dateOut'] = null;
    }

    // Get formatted filter values from form (handles date conversions and _ALL parameter)
    const filterFormData = this.getFilterValuesFromForm();

    // Initialize new filter parameters object
    const filterColumnParams: Partial<
      Record<keyof LogFilterParams, string | Date | number | boolean>
    > = {};

    // Process filter values from the form data
    Object.keys(filterFormData).forEach((key) => {
      const filterValue = filterFormData[key as keyof typeof filterFormData];

      // Only include non-empty values in the filter parameters
      if (
        filterValue !== undefined &&
        filterValue !== null &&
        filterValue !== ''
      ) {
        const columnName = key;
        filterColumnParams[columnName as keyof LogFilterParams] = filterValue;
      }
    });

    // Apply the assembled filter parameters to trigger search
    this.updateFilters(filterColumnParams);
  }

  /**
   * Updates the filters applied to the log search.
   * This method is called within the onSubmitFilters method.
   *
   * @param filterColumnParams The new filter parameters to apply.
   */
  public updateFilters(
    filterColumnParams: Partial<
      Record<keyof ApiReceivingLogRow, string | Date | Number | Boolean>
    >
  ) {
    const searchUpdate: ReceivingLogSearch = { ...this.logSearch };

    // Merge new filter parameters with existing ones
    const UpdatedFilterColumnParams = {
      ...this.logSearch.filterColumnParams,
      ...filterColumnParams,
    };

    searchUpdate.filterColumnParams = UpdatedFilterColumnParams;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Sorts the log search results by the specified column.
   * This method is called when the user clicks a column header.
   *
   * @param columnName The name of the column to sort by.
   */
  public onSortByColumn(columnName: string) {
    const searchUpdate: ReceivingLogSearch = { ...this.logSearch };
    searchUpdate.sortColumn = columnName;
    searchUpdate.sortOrderDesc = !searchUpdate.sortOrderDesc; // Toggle sort order
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Sorts by a column in an explicit direction (from the mobile sort sheet),
   * rather than toggling the current order the way a header click does.
   *
   * @param event The column to sort by and whether the order is descending.
   */
  public onSortByColumnDirection(event: { column: string; desc: boolean }) {
    const searchUpdate: ReceivingLogSearch = { ...this.logSearch };
    searchUpdate.sortColumn = event.column;
    searchUpdate.sortOrderDesc = event.desc;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Updates the page number for pagination.
   * This method is called when the user clicks to navigate forward or backward through the log pages.
   *
   * @param pageNumber The new page number to display.
   */
  public onUpdatePageNumber(pageNumber: number) {
    const rowStart = pageNumber * this.rowsPerPage;
    const rowEnd = rowStart + this.rowsPerPage - 1; // Ensure it's the last row on the page
    const searchUpdate: ReceivingLogSearch = { ...this.logSearch };
    searchUpdate.rowStart = rowStart;
    searchUpdate.rowEnd = rowEnd;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Updates the log search parameters and triggers a fetch operation.
   *
   * @param searchUpdate The updated search parameters.
   */
  public updateLogSearch(searchUpdate: ReceivingLogSearch) {
    const newLogFetchParams = { ...this.logSearch, ...searchUpdate };
    this.logSearch = newLogFetchParams;
    this._receivingLogService.fetchLogs(newLogFetchParams); // Fetch updated log data
  }

  /**
   * Determines if the edit field should be disabled based on the column properties and user role.
   *
   * @param column The column metadata to check.
   * @returns True if the field should be disabled, false otherwise.
   */
  public shouldShowClearField(column: any): boolean {
    if (isLogFormFieldLocked(column?.name, this.structuredRows)) { return false; }
    return !this.showArchivedLogs || (column.disabled && !this.userIsAdmin);
  }

  /**
   * Whether a field is read-only.
   *
   * Note the row-editor lock is NOT role-gated, unlike `column.disabled`. An admin
   * typing a quantity onto a structured log breaks the same invariant as anyone
   * else, and `PUT /V1/update-receiving-log` will accept it just as cheerfully:
   * its own quantity guard fires only on a MIXED-container log, so a single-type
   * structured log — 3 BOX and nothing else — takes a hand-typed value with no
   * complaint, and the string stops describing the rows that printed the labels.
   */
  public shouldDisableEditField(column: any): boolean {
    if (isLogFormFieldLocked(column?.name, this.structuredRows)) { return true; }
    return this.showArchivedLogs || (column.disabled && !this.userIsAdmin);
  }
}
